import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { BookOpen, SlidersHorizontal } from 'lucide-react';
import { get, post } from '@/shared/api/api_client';
import { useAuth } from '@/features/auth';
import { Navbar } from '@/widgets/Navbar';
import { LedgerTable } from '@/widgets/LedgerTable';
import { Button, Card, Input, Modal } from '@/shared/ui';
import type { LedgerTransaction } from '@/entities/ledger';
import type { FamilyMember } from '@/entities/user';

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------
interface LedgerResponse {
  entries: LedgerTransaction[];
}

interface FamilyMembersResponse {
  members: FamilyMember[];
}

// ---------------------------------------------------------------------------
// Adjust points schema
// ---------------------------------------------------------------------------
const adjustSchema = z.object({
  childId: z.string().min(1, 'Select a child'),
  points: z
    .number({ invalid_type_error: 'Must be a number' })
    .int('Must be a whole number')
    .refine((v) => v !== 0, 'Cannot be zero'),
  note: z.string().max(200).optional(),
});

type AdjustFormValues = z.infer<typeof adjustSchema>;

// ---------------------------------------------------------------------------
// Adjust Points Modal
// ---------------------------------------------------------------------------
function AdjustPointsModal({
  isOpen,
  onClose,
  familyId,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  familyId: string;
  children: FamilyMember[];
}): React.ReactElement {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    reset,
  } = useForm<AdjustFormValues>({
    resolver: zodResolver(adjustSchema),
    defaultValues: { points: 0 },
  });

  const onSubmit = async (data: AdjustFormValues) => {
    try {
      await post<void>(`/families/${familyId}/ledger/adjustment`, data);
      void queryClient.invalidateQueries({ queryKey: ['ledger', familyId] });
      void queryClient.invalidateQueries({ queryKey: ['balance', familyId] });
      reset();
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to adjust points.';
      setError('root', { message });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Manual Point Adjustment">
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-slate-700" htmlFor="adj-child">Child</label>
          <select
            id="adj-child"
            className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            {...register('childId')}
          >
            <option value="">Select a child...</option>
            {children.filter((c) => c.uid).map((c) => (
              <option key={c.memberId} value={c.uid!}>{c.displayName}</option>
            ))}
          </select>
          {errors.childId && (
            <p className="text-xs text-danger-600">{errors.childId.message}</p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-slate-700" htmlFor="adj-points">
            Points (positive = add, negative = deduct)
          </label>
          <input
            id="adj-points"
            type="number"
            placeholder="e.g. 50 or -25"
            className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            {...register('points', { valueAsNumber: true })}
          />
          {errors.points && (
            <p className="text-xs text-danger-600">{errors.points.message}</p>
          )}
        </div>

        <Input
          label="Note"
          placeholder="Reason for adjustment..."
          error={errors.note?.message}
          {...register('note')}
        />

        {errors.root && (
          <p role="alert" className="text-xs text-danger-600">{errors.root.message}</p>
        )}

        <div className="flex gap-2 pt-2">
          <Button type="submit" isLoading={isSubmitting} fullWidth>Apply Adjustment</Button>
          <Button variant="ghost" onClick={onClose} type="button">Cancel</Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// LedgerPage
// ---------------------------------------------------------------------------
export default function LedgerPage(): React.ReactElement {
  const { appUser } = useAuth();
  const familyId = appUser?.familyId ?? '';
  const role = appUser?.role ?? 'child';
  const childId = appUser?.uid ?? '';

  const [selectedChildId, setSelectedChildId] = useState<string>('all');
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Queries
  const { data: membersData } = useQuery<FamilyMembersResponse>({
    queryKey: ['members', familyId],
    queryFn: () => get<FamilyMembersResponse>(`/families/${familyId}/members`),
    enabled: Boolean(familyId && role === 'parent'),
  });

  // Build query params
  const buildParams = () => {
    const params = new URLSearchParams();
    if (role === 'child') {
      params.set('childId', childId);
    } else if (selectedChildId !== 'all') {
      params.set('childId', selectedChildId);
    }
    if (dateFrom) params.set('from', dateFrom);
    if (dateTo) params.set('to', dateTo);
    return params.toString();
  };

  const { data: txData, isLoading: txLoading } = useQuery<LedgerResponse>({
    queryKey: ['ledger', familyId, selectedChildId, dateFrom, dateTo, role === 'child' ? childId : ''],
    queryFn: () => get<LedgerResponse>(`/families/${familyId}/ledger?${buildParams()}`),
    enabled: Boolean(familyId),
  });

  const members = membersData?.members ?? [];
  const childMembers = members.filter((m) => m.role === 'child');
  const transactions = txData?.entries ?? [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <BookOpen size={22} className="text-primary-600" aria-hidden="true" />
            <h1 className="text-xl font-bold text-slate-900">Ledger</h1>
          </div>
          {role === 'parent' && (
            <Button
              variant="secondary"
              onClick={() => setShowAdjustModal(true)}
              leftIcon={<SlidersHorizontal size={16} />}
            >
              Manual Adjustment
            </Button>
          )}
        </div>

        {/* Filters (parent only) */}
        {role === 'parent' && (
          <Card padding="sm" className="flex flex-wrap gap-4 items-end">
            <div className="flex flex-col gap-1 min-w-[160px]">
              <label className="text-xs font-medium text-slate-600" htmlFor="child-select">Child</label>
              <select
                id="child-select"
                value={selectedChildId}
                onChange={(e) => setSelectedChildId(e.target.value)}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All children</option>
                {childMembers.map((c) => (
                  <option key={c.memberId} value={c.memberId}>{c.displayName}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-600" htmlFor="date-from">From</label>
              <input
                id="date-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-600" htmlFor="date-to">To</label>
              <input
                id="date-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            {(dateFrom || dateTo || selectedChildId !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setDateFrom(''); setDateTo(''); setSelectedChildId('all'); }}
              >
                Clear filters
              </Button>
            )}
          </Card>
        )}

        {/* Ledger table */}
        <LedgerTable
          transactions={transactions}
          isLoading={txLoading}
          emptyMessage={
            role === 'child'
              ? 'No transactions yet. Complete some chores to earn points!'
              : 'No transactions found for the selected filters.'
          }
        />
      </main>

      {/* Adjust points modal (parent only) */}
      <AdjustPointsModal
        isOpen={showAdjustModal}
        onClose={() => setShowAdjustModal(false)}
        familyId={familyId}
        children={childMembers}
      />
    </div>
  );
}
